/**
 * Timeline Editor Modal (canvas-v2)
 *
 * Full-screen portal with 4-panel CSS grid layout:
 * - ClipPanel (left), VideoPreviewPlayer (center), AdjustPanel (right)
 * - TimelineEditor (bottom)
 *
 * Differences from V1:
 * - 4-panel grid layout (not vertical stack)
 * - Uses executeNode (fire-and-forget via Orchestrator)
 * - Render config includes speed/track_index per clip
 * - Ctrl/Cmd+C/V copy/paste shortcuts
 * - useAuthenticatedMediaUrl for thumbnail generation URLs
 */

import { createPortal } from 'react-dom'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { TimelineEditor } from './TimelineEditor'
import { VideoPreviewPlayer } from './VideoPreviewPlayer'
import { ClipPanel } from './ClipPanel'
import { AdjustPanel } from './AdjustPanel'
import { useTimelineState } from './useTimelineState'
import { useTimelinePlayback } from './useTimelinePlayback'
import { useBatchClipThumbnails } from './useClipThumbnails'
import { useTimelineYjsSync } from './useTimelineYjsSync'
import { useYDoc } from '../../../../hooks/useYDoc'
import { buildProjectAssetUrl, executeNode, authenticatedFetch } from '../../../../api/client'
import { useAuth } from '../../../../contexts/AuthContext'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useCanvasOptional } from '../../CanvasContext'
import type { TimelineClip, TimelineAudioClip, TimelineRenderConfig } from './types'

interface TimelineEditorModalProps {
  /** VideoEditorNode ID */
  nodeId: string
  /** Connected video clips */
  videoClips: Array<{ id: string; path: string; name: string }>
  /** Connected audio clips */
  audioClips: Array<{ id: string; path: string; name: string; role?: 'original' | 'replacement' | 'sfx' }>
  /** Close callback */
  onClose: () => void
}

export function TimelineEditorModal({
  nodeId,
  videoClips,
  audioClips: audioClipRefs,
  onClose,
}: TimelineEditorModalProps) {
  const projectId = useProjectId()
  const canvas = useCanvasOptional()
  const { session } = useAuth()
  const token = session?.access_token ?? null

  const [isRendering, setIsRendering] = useState(false)
  const [renderProgress, setRenderProgress] = useState(0)
  const [renderMessage, setRenderMessage] = useState('')

  // Convert video clips to TimelineClip format
  const initialClips: TimelineClip[] = useMemo(
    () =>
      videoClips.map((clip, index) => ({
        id: clip.id,
        sourceNodeId: clip.id,
        sourcePath: clip.path,
        originalDuration: 5,
        trimStart: 0,
        trimEnd: 0,
        effectiveDuration: 5,
        order: index,
        label: clip.name,
        thumbnails: [],
        speed: 1.0,
      })),
    [videoClips]
  )

  // Convert audio clips to TimelineAudioClip format
  const initialAudioClips: TimelineAudioClip[] = useMemo(
    () =>
      audioClipRefs.map((af) => ({
        id: af.id,
        sourceNodeId: af.id,
        sourcePath: af.path,
        originalDuration: 15,
        trimStart: 0,
        trimEnd: 0,
        effectiveDuration: 15,
        volume: 0.8,
        muted: false,
        startOffset: 0,
        role: (af.role || 'original') as 'original' | 'replacement' | 'sfx',
        speed: 1.0,
        trackIndex: 0,
      })),
    [audioClipRefs]
  )

  // Stable keys: only change when clip IDs or paths actually change
  const videoClipsKey = videoClips.map((c) => `${c.id}:${c.path}`).join('|')
  const audioFileKey = audioClipRefs.map((a) => `${a.id}:${a.path}`).join('|')
  const videoClipsRef = useRef(videoClips)
  videoClipsRef.current = videoClips
  const audioClipRefsRef = useRef(audioClipRefs)
  audioClipRefsRef.current = audioClipRefs

  // Timeline state management
  const {
    timelineState,
    addClip,
    addAudioClip,
    setPlayhead,
    setZoom,
    togglePlayPause,
    selectClip,
    reorderClip,
    trimClip,
    trimClipPreview,
    updateClipThumbnails,
    setAudioVolume,
    toggleAudioMute,
    setAudioOffset,
    setClipSpeed,
    setAudioSpeed,
    copyClip,
    pasteClip,
    deleteSelectedClip,
    splitSelectedClip,
    undo,
    redo,
    canUndo,
    canRedo,
    restoreState,
  } = useTimelineState()

  // Ref for accessing latest timelineState in async callbacks
  const timelineStateRef = useRef(timelineState)
  timelineStateRef.current = timelineState
  const clipsLength = timelineState.clips.length

  // Load actual video and audio durations from metadata
  const loadedDurationsRef = useRef<Map<string, number>>(new Map())
  const [durationsLoaded, setDurationsLoaded] = useState(false)

  // Reset when source clips/audio change so durations are re-fetched
  const prevKeysRef = useRef(`${videoClipsKey}|${audioFileKey}`)
  useEffect(() => {
    const currentKeys = `${videoClipsKey}|${audioFileKey}`
    if (prevKeysRef.current !== currentKeys) {
      prevKeysRef.current = currentKeys
      loadedDurationsRef.current = new Map()
      setDurationsLoaded(false)
    }
  }, [videoClipsKey, audioFileKey])

  useEffect(() => {
    const clips = videoClipsRef.current
    const audioRefs = audioClipRefsRef.current
    if (!projectId || (clips.length === 0 && audioRefs.length === 0) || durationsLoaded) return

    let cancelled = false
    const loadDurations = async () => {
      const durations = new Map<string, number>()

      const videoPromises = clips.map(async (clip) => {
        try {
          const url = buildProjectAssetUrl(projectId, clip.path)
          const response = await authenticatedFetch(url)
          const blob = await response.blob()
          const blobUrl = URL.createObjectURL(blob)

          const duration = await new Promise<number>((resolve, reject) => {
            const video = document.createElement('video')
            video.preload = 'metadata'
            video.onloadedmetadata = () => {
              resolve(video.duration)
              URL.revokeObjectURL(blobUrl)
            }
            video.onerror = () => {
              URL.revokeObjectURL(blobUrl)
              reject(new Error('Failed to load video metadata'))
            }
            video.src = blobUrl
          })

          durations.set(clip.id, duration)
        } catch (err) {
          console.error(`Failed to load duration for ${clip.name}:`, err)
        }
      })

      const audioPromises = audioRefs.map(async (af) => {
        try {
          const url = buildProjectAssetUrl(projectId, af.path)
          const response = await authenticatedFetch(url)
          const blob = await response.blob()
          const blobUrl = URL.createObjectURL(blob)

          const duration = await new Promise<number>((resolve, reject) => {
            const audio = new Audio()
            audio.preload = 'metadata'
            audio.onloadedmetadata = () => {
              resolve(audio.duration)
              URL.revokeObjectURL(blobUrl)
            }
            audio.onerror = () => {
              URL.revokeObjectURL(blobUrl)
              reject(new Error('Failed to load audio metadata'))
            }
            audio.src = blobUrl
          })

          durations.set(af.id, duration)
        } catch (err) {
          console.error(`Failed to load audio duration for ${af.name}:`, err)
        }
      })

      await Promise.all([...videoPromises, ...audioPromises])

      if (!cancelled) {
        loadedDurationsRef.current = durations
        setDurationsLoaded(true)
      }
    }

    loadDurations()
    return () => {
      cancelled = true
    }
  }, [projectId, videoClipsKey, audioFileKey, durationsLoaded, token])

  // Fingerprint that changes when any clip has a stale/placeholder duration
  const clipDurationFingerprint = timelineState.clips
    .map((c) => `${c.id}:${c.originalDuration}`)
    .join(',')

  // Apply loaded durations to video and audio clips once both are ready
  useEffect(() => {
    if (!durationsLoaded || clipsLength === 0) return
    const durations = loadedDurationsRef.current
    if (durations.size === 0) return

    const currentState = timelineStateRef.current
    const currentClips = currentState.clips
    const currentAudioClips = currentState.audioClips

    const videoNeedsUpdate = currentClips.some((clip) => {
      const realDuration = durations.get(clip.id)
      return realDuration && realDuration !== clip.originalDuration
    })
    const audioNeedsUpdate = currentAudioClips.some((clip) => {
      const realDuration = durations.get(clip.id)
      return realDuration && realDuration !== clip.originalDuration
    })

    if (videoNeedsUpdate || audioNeedsUpdate) {
      const updatedClips = currentClips.map((clip) => {
        const realDuration = durations.get(clip.id)
        if (realDuration && realDuration !== clip.originalDuration) {
          return {
            ...clip,
            originalDuration: realDuration,
            effectiveDuration: realDuration - clip.trimStart - clip.trimEnd,
          }
        }
        return clip
      })

      const updatedAudioClips = currentAudioClips.map((clip) => {
        const realDuration = durations.get(clip.id)
        if (realDuration && realDuration !== clip.originalDuration) {
          return {
            ...clip,
            originalDuration: realDuration,
            effectiveDuration: realDuration - clip.trimStart - clip.trimEnd,
          }
        }
        return clip
      })

      const totalDuration = updatedClips.reduce((sum, c) => sum + c.effectiveDuration, 0)
      restoreState({
        ...currentState,
        clips: updatedClips,
        audioClips: updatedAudioClips,
        totalDuration,
      })
    }
  }, [durationsLoaded, clipsLength, clipDurationFingerprint, restoreState])

  // Yjs document connection for draft sync
  const { ydoc, synced } = useYDoc({
    projectId: projectId || '',
    canvasId: 'agent',
    enabled: !!projectId,
  })

  // Compute valid source IDs from edge-connected clips for Yjs filtering.
  // This prevents stale (disconnected) clips from flashing on modal open.
  const validVideoSourceIds = useMemo(
    () => new Set(videoClips.map(c => c.id)),
    [videoClips]
  )
  const validAudioSourceIds = useMemo(
    () => new Set(audioClipRefs.map(c => c.id)),
    [audioClipRefs]
  )

  // Sync timeline state to Yjs with debouncing
  useTimelineYjsSync({
    ydoc,
    synced,
    timelineState,
    nodeId,
    onRestoreState: restoreState,
    debounceMs: 2000,
    validVideoSourceIds,
    validAudioSourceIds,
  })

  // Initialize clips on mount, or merge new clips from edges into Yjs-restored state
  const mergeAttemptedRef = useRef(false)
  useEffect(() => {
    if (initialClips.length === 0) return

    if (timelineState.clips.length === 0) {
      // Fresh start — no Yjs state, load all initial clips
      mergeAttemptedRef.current = false
      const totalDuration = initialClips.reduce((sum, clip) => sum + clip.effectiveDuration, 0)
      restoreState({
        clips: initialClips,
        audioClips: initialAudioClips,
        playheadPosition: 0,
        zoomLevel: 50,
        isPlaying: false,
        selectedClipId: null,
        selectedClipType: null,
        totalDuration,
        copiedClip: null,
        copiedClipType: null,
      })
    } else if (!mergeAttemptedRef.current) {
      // Merge: add new clips from edges AND prune stale clips no longer edge-connected
      mergeAttemptedRef.current = true
      const currentState = timelineStateRef.current
      const initialSourceIds = new Set(initialClips.map(c => c.sourceNodeId))
      const existingSourceIds = new Set(currentState.clips.map(c => c.sourceNodeId))
      const newClips = initialClips.filter(c => !existingSourceIds.has(c.sourceNodeId))

      // Prune clips whose sourceNodeId is no longer in the edge-connected set
      const prunedClips = currentState.clips.filter(c => initialSourceIds.has(c.sourceNodeId))

      const maxOrder = prunedClips.reduce((max, c) => Math.max(max, c.order), -1)
      const mergedClips = [
        ...prunedClips,
        ...newClips.map((clip, idx) => ({ ...clip, order: maxOrder + 1 + idx })),
      ]
      const totalDuration = mergedClips.reduce((sum, c) => sum + c.effectiveDuration, 0)

      // Also merge + prune audio clips
      const initialAudioSourceIds = new Set(initialAudioClips.map(c => c.sourceNodeId))
      const existingAudioSourceIds = new Set(currentState.audioClips.map(c => c.sourceNodeId))
      const newAudioClips = initialAudioClips.filter(c => !existingAudioSourceIds.has(c.sourceNodeId))
      const prunedAudioClips = currentState.audioClips.filter(c => initialAudioSourceIds.has(c.sourceNodeId))

      if (newClips.length > 0 || prunedClips.length !== currentState.clips.length ||
          newAudioClips.length > 0 || prunedAudioClips.length !== currentState.audioClips.length) {
        restoreState({
          ...currentState,
          clips: mergedClips,
          audioClips: [...prunedAudioClips, ...newAudioClips],
          totalDuration,
        })
      }
    }
  }, [initialClips, initialAudioClips, timelineState.clips.length, restoreState])

  // Generate thumbnails for clips with authenticated URLs
  const clipIds = timelineState.clips.map((c) => c.id).join(',')
  const clipsForThumbnails = useMemo(() => {
    if (!projectId) return []

    return timelineState.clips.map((clip) => ({
      ...clip,
      sourcePath: (() => {
        const base = buildProjectAssetUrl(projectId, clip.sourcePath)
        return token && !base.startsWith('https://') ? `${base}?token=${token}` : base
      })(),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, clipIds, token])

  // Generate thumbnails in batch
  const thumbnailMap = useBatchClipThumbnails(clipsForThumbnails)

  // Update clips with generated thumbnails
  useEffect(() => {
    thumbnailMap.forEach((thumbnails, clipId) => {
      const clip = timelineState.clips.find((c) => c.id === clipId)
      if (thumbnails.length > 0 && clip && clip.thumbnails.length === 0) {
        updateClipThumbnails(clipId, thumbnails)
      }
    })
  }, [thumbnailMap, timelineState.clips, updateClipThumbnails])

  // Playback hook
  const {
    togglePlayPause: handleTogglePlayPause,
    scrub,
    jumpToStart,
    jumpToEnd,
    playheadPositionRef,
  } = useTimelinePlayback({
    timelineState,
    setPlayhead,
    togglePlayPause,
  })

  // Selected clip objects for AdjustPanel
  const selectedVideoClip = useMemo(
    () =>
      timelineState.selectedClipType === 'video'
        ? timelineState.clips.find((c) => c.id === timelineState.selectedClipId) ?? null
        : null,
    [timelineState.clips, timelineState.selectedClipId, timelineState.selectedClipType]
  )

  const selectedAudioClip = useMemo(
    () =>
      timelineState.selectedClipType === 'audio'
        ? timelineState.audioClips.find((c) => c.id === timelineState.selectedClipId) ?? null
        : null,
    [timelineState.audioClips, timelineState.selectedClipId, timelineState.selectedClipType]
  )

  // Route speed changes to correct handler based on clip type
  const handleSpeedChange = useCallback(
    (clipId: string, speed: number) => {
      const isAudio = timelineState.audioClips.some((c) => c.id === clipId)
      if (isAudio) {
        setAudioSpeed(clipId, speed)
      } else {
        setClipSpeed(clipId, speed)
      }
    },
    [timelineState.audioClips, setAudioSpeed, setClipSpeed]
  )

  // ScrollToClip handler — selects the clip (which highlights it in the timeline)
  const handleScrollToClip = useCallback(
    (clipId: string, clipType: 'video' | 'audio') => {
      selectClip(clipId, clipType)
    },
    [selectClip]
  )

  // Add a reference clip to the timeline
  const handleAddClipToTimeline = useCallback(
    (clipId: string, clipType: 'video' | 'audio') => {
      if (clipType === 'video') {
        const ref = videoClips.find(c => c.id === clipId)
        if (!ref) return
        addClip({
          id: crypto.randomUUID(),
          sourceNodeId: ref.id,
          sourcePath: ref.path,
          originalDuration: loadedDurationsRef.current.get(ref.id) ?? 5,
          trimStart: 0,
          trimEnd: 0,
          effectiveDuration: loadedDurationsRef.current.get(ref.id) ?? 5,
          order: timelineStateRef.current.clips.length,
          label: ref.name,
          thumbnails: [],
          speed: 1.0,
        })
      } else {
        const ref = audioClipRefs.find(c => c.id === clipId)
        if (!ref) return
        addAudioClip({
          id: crypto.randomUUID(),
          sourceNodeId: ref.id,
          sourcePath: ref.path,
          originalDuration: loadedDurationsRef.current.get(ref.id) ?? 15,
          trimStart: 0,
          trimEnd: 0,
          effectiveDuration: loadedDurationsRef.current.get(ref.id) ?? 15,
          volume: 0.8,
          muted: false,
          startOffset: 0,
          role: (ref.role || 'original') as 'original' | 'replacement' | 'sfx',
          speed: 1.0,
          trackIndex: 0,
        })
      }
    },
    [videoClips, audioClipRefs, addClip, addAudioClip]
  )

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      // Escape: Close modal (blocked during render)
      if (e.code === 'Escape') {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (!isRendering) {
          onClose()
        }
        return
      }

      // Space: Play/Pause
      if (e.code === 'Space') {
        e.preventDefault()
        e.stopImmediatePropagation()
        handleTogglePlayPause()
        return
      }

      // S: Split selected clip at playhead (type-aware)
      if (e.code === 'KeyS' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        splitSelectedClip()
        return
      }

      // Delete/Backspace: Remove selected clip (type-aware)
      if ((e.code === 'Delete' || e.code === 'Backspace') && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        deleteSelectedClip()
        return
      }

      // Ctrl/Cmd+Z: Undo
      if (e.code === 'KeyZ' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (canUndo) {
          undo()
        }
        return
      }

      // Ctrl/Cmd+Shift+Z: Redo
      if (e.code === 'KeyZ' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (canRedo) {
          redo()
        }
        return
      }

      // Ctrl/Cmd+C: Copy selected clip
      if (e.code === 'KeyC' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        copyClip()
        return
      }

      // Ctrl/Cmd+V: Paste copied clip
      if (e.code === 'KeyV' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        pasteClip()
        return
      }

      // Left Arrow: Move playhead back 1 second
      if (e.code === 'ArrowLeft' && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        scrub(-1)
        return
      }

      // Right Arrow: Move playhead forward 1 second
      if (e.code === 'ArrowRight' && !e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        scrub(1)
        return
      }

      // Shift+Left: Fine scrub back 0.1 second
      if (e.code === 'ArrowLeft' && e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        scrub(-0.1)
        return
      }

      // Shift+Right: Fine scrub forward 0.1 second
      if (e.code === 'ArrowRight' && e.shiftKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        scrub(0.1)
        return
      }

      // Home: Jump to start
      if (e.code === 'Home') {
        e.preventDefault()
        e.stopImmediatePropagation()
        jumpToStart()
        return
      }

      // End: Jump to end
      if (e.code === 'End') {
        e.preventDefault()
        e.stopImmediatePropagation()
        jumpToEnd()
        return
      }
    }

    // Use capture phase on document so we fire BEFORE ReactFlow's bubble-phase
    // keydown listener on document. This prevents Delete/Backspace from deleting
    // the canvas node while the timeline editor modal is open.
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [
    handleTogglePlayPause,
    splitSelectedClip,
    deleteSelectedClip,
    canUndo,
    canRedo,
    undo,
    redo,
    copyClip,
    pasteClip,
    scrub,
    jumpToStart,
    jumpToEnd,
    isRendering,
    onClose,
  ])

  // Handle render button click
  const handleRender = useCallback(async () => {
    if (!projectId) return
    if (timelineState.clips.length === 0) return

    setIsRendering(true)
    setRenderProgress(0)
    setRenderMessage('Preparing timeline...')

    try {
      const renderConfig: TimelineRenderConfig = {
        operation: 'timeline_render',
        timeline: {
          clips: timelineState.clips.map((clip) => ({
            source_path: clip.sourcePath,
            start_time: clip.trimStart,
            end_time: clip.originalDuration - clip.trimEnd,
            order: clip.order,
            speed: clip.speed,
          })),
          audio:
            timelineState.audioClips.length === 1
              ? {
                  source_path: timelineState.audioClips[0].sourcePath,
                  start_time: timelineState.audioClips[0].trimStart,
                  end_time:
                    timelineState.audioClips[0].originalDuration - timelineState.audioClips[0].trimEnd,
                  volume: timelineState.audioClips[0].volume,
                }
              : null,
          audio_tracks:
            timelineState.audioClips.length > 0
              ? timelineState.audioClips
                  .filter((c) => !c.muted)
                  .map((c) => ({
                    source_path: c.sourcePath,
                    start_time: c.trimStart,
                    end_time: c.originalDuration - c.trimEnd,
                    volume: c.volume,
                    role: c.role,
                    start_offset: c.startOffset,
                    speed: c.speed,
                    track_index: c.trackIndex,
                  }))
              : null,
          total_duration: timelineState.totalDuration,
        },
      }

      // Write timeline config to YDoc before executing
      canvas?.updateNodeData(nodeId, { in_timeline_config: renderConfig })
      await executeNode(projectId, nodeId, 'timeline_render')

      // executeNode returns immediately. Backend updates node status via Yjs.
      setRenderMessage('Render started — processing on server...')

      // Auto-close rendering state after a short delay
      setTimeout(() => {
        setIsRendering(false)
        setRenderProgress(0)
        setRenderMessage('')
      }, 3000)
    } catch (err) {
      console.error('Render failed:', err)
      setIsRendering(false)
      setRenderProgress(0)
      setRenderMessage('')
    }
  }, [projectId, nodeId, timelineState, canvas])

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        if (!isRendering && e.target === e.currentTarget) onClose()
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        className="relative bg-card rounded-lg shadow-2xl overflow-hidden"
        style={{
          width: '90vw',
          height: '90vh',
          minWidth: '800px',
          minHeight: '600px',
          display: 'grid',
          gridTemplateColumns: '250px 1fr 280px',
          gridTemplateRows: 'auto 1fr 300px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Row 1: Modal Header (spans all 3 columns) */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b border-border bg-card-hover"
          style={{ gridColumn: '1 / -1' }}
        >
          <h2 className="text-lg font-semibold text-foreground">Timeline Editor</h2>
          <div className="flex items-center gap-3">
            {/* Render Button */}
            <button
              onClick={handleRender}
              disabled={isRendering || videoClips.length === 0 || !durationsLoaded}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                isRendering
                  ? 'bg-yellow-500/20 text-yellow-400 cursor-wait'
                  : videoClips.length === 0 || !durationsLoaded
                    ? 'bg-surface-1 text-foreground/30 cursor-not-allowed'
                    : 'bg-teal-600 hover:bg-teal-700 text-foreground'
              }`}
            >
              {isRendering ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Rendering...
                </>
              ) : !durationsLoaded ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Render
                </>
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              disabled={isRendering}
              className={`p-2 rounded-lg transition-colors ${
                isRendering
                  ? 'text-foreground/20 cursor-not-allowed'
                  : 'text-text-muted hover:text-foreground hover:bg-surface-2'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 2, Col 1: ClipPanel (left) */}
        <div className="border-r border-border overflow-hidden">
          <ClipPanel
            clips={timelineState.clips}
            audioClips={timelineState.audioClips}
            referenceVideoClips={videoClips}
            referenceAudioClips={audioClipRefs}
            selectedClipId={timelineState.selectedClipId}
            onSelectClip={selectClip}
            onScrollToClip={handleScrollToClip}
            onAddClipToTimeline={handleAddClipToTimeline}
          />
        </div>

        {/* Row 2, Col 2: VideoPreviewPlayer (center) */}
        <div className="flex flex-col bg-canvas p-4 min-h-0">
          <div className="flex-1 min-h-0">
            <VideoPreviewPlayer
              clips={timelineState.clips}
              audioClips={timelineState.audioClips}
              playheadPosition={timelineState.playheadPosition}
              projectId={projectId}
              isPlaying={timelineState.isPlaying}
              onVideoEnd={() => togglePlayPause()}
            />
          </div>
          {/* Playback Controls */}
          <div className="flex-shrink-0 flex items-center justify-center gap-4 mt-2">
            <button
              onClick={jumpToStart}
              disabled={timelineState.playheadPosition === 0}
              className="p-2 rounded-lg bg-surface-1 hover:bg-surface-2 text-text-secondary hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Jump to start (Home)"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
              </svg>
            </button>
            <button
              onClick={handleTogglePlayPause}
              disabled={timelineState.clips.length === 0}
              className="p-3 rounded-lg bg-surface-2 hover:bg-white/15 text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Play/Pause (Space)"
            >
              {timelineState.isPlaying ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            <button
              onClick={jumpToEnd}
              disabled={timelineState.playheadPosition >= timelineState.totalDuration}
              className="p-2 rounded-lg bg-surface-1 hover:bg-surface-2 text-text-secondary hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Jump to end (End)"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 2, Col 3: AdjustPanel (right) */}
        <div className="border-l border-border overflow-hidden">
          <AdjustPanel
            selectedVideoClip={selectedVideoClip}
            selectedAudioClip={selectedAudioClip}
            selectedClipType={timelineState.selectedClipType}
            onSpeedChange={handleSpeedChange}
            onVolumeChange={setAudioVolume}
            onToggleMute={toggleAudioMute}
          />
        </div>

        {/* Row 3: TimelineEditor (bottom, spans all 3 columns) */}
        <div className="border-t border-border" style={{ gridColumn: '1 / -1' }}>
          <TimelineEditor
            timelineState={timelineState}
            onSelectClip={selectClip}
            onSetPlayhead={setPlayhead}
            onSetZoom={setZoom}
            onReorderClip={reorderClip}
            onTrimClip={trimClip}
            onTrimClipPreview={trimClipPreview}
            onAudioOffsetChange={setAudioOffset}
            playheadPositionRef={playheadPositionRef}
            readOnly={isRendering}
            onSplitSelectedClip={splitSelectedClip}
            onDeleteSelectedClip={deleteSelectedClip}
            onCopyClip={copyClip}
            onPasteClip={pasteClip}
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
          />
        </div>

        {/* Render Progress Overlay */}
        {isRendering && (
          <div
            className="absolute inset-0 bg-black/50 flex items-center justify-center z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-card rounded-xl p-8 flex flex-col items-center gap-4 max-w-sm">
              <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" />
              <div className="text-sm text-text-secondary text-center">
                {renderMessage || 'Processing clips... Please wait.'}
              </div>
              {renderProgress > 0 && (
                <div className="w-full">
                  <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-teal-400 transition-all duration-300 ease-out"
                      style={{ width: `${renderProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
